import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiClient from '../utils/axiosConfig';
import { 
  BsCheckCircle, 
  BsXCircle,
  BsInfoCircle
} from 'react-icons/bs';
import { FaDownload, FaTrash, FaExclamationTriangle } from 'react-icons/fa';

const ConsentManagement = ({ email, onConsentUpdate }) => {
  const [consents, setConsents] = useState({
    newsletter: false,
    marketing: false,
    personalization: false,
    analytics: false,
    third_party_sharing: false
  });
  
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  // Load current consent status when component mounts
  useEffect(() => {
    if (email) {
      loadConsentStatus();
    }
  }, [email]);

  const loadConsentStatus = async () => {
    try {
      const response = await apiClient.get(`/consent/status/${email}`);
      if (response.data.success && response.data.data.purposes) {
        const purposes = response.data.data.purposes;
        setConsents({
          newsletter: purposes.newsletter?.granted || false,
          marketing: purposes.marketing?.granted || false,
          personalization: purposes.personalization?.granted || false,
          analytics: purposes.analytics?.granted || false,
          third_party_sharing: purposes.third_party_sharing?.granted || false
        });
      }
    } catch (error) {
      console.error('Error loading consent status:', error);
      toast.error('Failed to load consent status');
    } finally {
      setInitialLoad(false);
    }
  };

  const handleConsentChange = (purpose, granted) => {
    setConsents(prev => ({
      ...prev,
      [purpose]: granted
    }));
  };

  const saveConsents = async () => {
    if (!email) {
      toast.error('Email is required to save consent preferences');
      return;
    }

    setLoading(true);
    
    try {
      // Transform consents to the format expected by the backend
      const purposes = Object.keys(consents).map(key => ({
        purpose: key,
        granted: consents[key]
      }));

      const response = await apiClient.post('/consent/update', {
        email,
        purposes
      });

      if (response.data.success) {
        toast.success('Consent preferences saved successfully');
        if (onConsentUpdate) {
          onConsentUpdate(consents);
        }
      } else {
        toast.error(response.data.message || 'Failed to save consent preferences');
      }
    } catch (error) {
      console.error('Error saving consents:', error);
      toast.error(error.response?.data?.message || 'Failed to save consent preferences');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoad) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 my-4">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 my-4">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Privacy Preferences</h3>
        <p className="text-gray-600">
          Manage your privacy preferences and control how we use your information.
        </p>
      </div>

      <div className="space-y-4">
        {/* Newsletter Consent */}
        <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
          <div className="flex-1">
            <div className="flex items-center">
              <h4 className="font-semibold text-gray-900">Newsletter Subscription</h4>
              <div className="ml-2 text-gray-500">
                <BsInfoCircle className="text-sm" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Receive updates on new products, special offers, and mythological stories.
            </p>
          </div>
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={consents.newsletter}
                onChange={(e) => handleConsentChange('newsletter', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        {/* Marketing Consent */}
        <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
          <div className="flex-1">
            <div className="flex items-center">
              <h4 className="font-semibold text-gray-900">Marketing Communications</h4>
              <div className="ml-2 text-gray-500">
                <BsInfoCircle className="text-sm" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Receive promotional emails about special offers and discounts.
            </p>
          </div>
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={consents.marketing}
                onChange={(e) => handleConsentChange('marketing', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        {/* Personalization Consent */}
        <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
          <div className="flex-1">
            <div className="flex items-center">
              <h4 className="font-semibold text-gray-900">Personalization</h4>
              <div className="ml-2 text-gray-500">
                <BsInfoCircle className="text-sm" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Allow us to personalize your shopping experience based on your preferences.
            </p>
          </div>
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={consents.personalization}
                onChange={(e) => handleConsentChange('personalization', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        {/* Analytics Consent */}
        <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
          <div className="flex-1">
            <div className="flex items-center">
              <h4 className="font-semibold text-gray-900">Analytics</h4>
              <div className="ml-2 text-gray-500">
                <BsInfoCircle className="text-sm" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Help us improve our website by allowing us to collect anonymous usage data.
            </p>
          </div>
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={consents.analytics}
                onChange={(e) => handleConsentChange('analytics', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        {/* Third Party Sharing Consent */}
        <div className="flex items-start p-4 border border-gray-200 rounded-lg hover:border-red-300 transition-colors">
          <div className="flex-1">
            <div className="flex items-center">
              <h4 className="font-semibold text-gray-900">Third-Party Sharing</h4>
              <div className="ml-2 text-gray-500">
                <BsInfoCircle className="text-sm" />
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Allow us to share your information with trusted partners for marketing purposes.
            </p>
          </div>
          <div className="flex items-center">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={consents.third_party_sharing}
                onChange={(e) => handleConsentChange('third_party_sharing', e.target.checked)}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-4">
        <button
          onClick={saveConsents}
          disabled={loading}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </span>
          ) : (
            'Save Preferences'
          )}
        </button>
        
        <button
          onClick={() => {
            setConsents({
              newsletter: false,
              marketing: false,
              personalization: false,
              analytics: false,
              third_party_sharing: false
            });
          }}
          className="px-6 py-3 bg-gray-100 text-gray-700 font-bold rounded-full shadow-lg hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-all"
        >
          Decline All
        </button>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start">
          <BsInfoCircle className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            You can update your preferences at any time. For more information about how we use your data, please see our Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ConsentManagement;